import React, { useState, useEffect, useCallback } from 'react';
import Navigation from '@/components/shared/Navigation';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Trophy, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { playingPositionsApi } from '@/lib/api';

interface PlayingPosition {
  id: number;
  name: string;
  description: string;
  abbreviation: string;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
  members_count?: number;
}

interface PlayingPositionFormData {
  name: string;
  description: string;
  abbreviation: string;
  is_active: boolean;
  sort_order: number;
}

const AdminPlayingPositions = () => {
  const [playingPositions, setPlayingPositions] = useState<PlayingPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingPosition, setEditingPosition] = useState<PlayingPosition | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<PlayingPositionFormData>({
    name: '',
    description: '',
    abbreviation: '',
    is_active: true,
    sort_order: 1
  });

  const fetchPlayingPositions = useCallback(async () => {
    try {
      setLoading(true);
      const response: any = await playingPositionsApi.getAll();
      
      let positions: PlayingPosition[] = [];
      if (Array.isArray(response)) {
        positions = response;
      } else if (response && Array.isArray(response.data)) {
        positions = response.data;
      } else {
        console.warn('Unexpected response format:', response);
        positions = [];
      }
      
      // Sort by sort_order
      positions.sort((a, b) => a.sort_order - b.sort_order);
      setPlayingPositions(positions);
    } catch (error) {
      console.error('Error fetching playing positions:', error);
      toast({
        title: "Error",
        description: "Failed to fetch playing positions",
        variant: "destructive"
      });
      setPlayingPositions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlayingPositions();
  }, [fetchPlayingPositions]);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      abbreviation: '',
      is_active: true,
      sort_order: playingPositions.length + 1
    });
  };

  const handleCreatePosition = async () => {
    if (!formData.name.trim() || !formData.abbreviation.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter position name and abbreviation",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const positionData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        abbreviation: formData.abbreviation.trim().toUpperCase(),
        is_active: formData.is_active,
        sort_order: formData.sort_order
      };
      
      await playingPositionsApi.create(positionData);
      
      toast({
        title: "Success",
        description: "Playing position created successfully"
      });
      
      setIsCreateDialogOpen(false);
      resetForm();
      fetchPlayingPositions();
    } catch (error: any) {
      console.error('Error creating playing position:', error);
      
      let errorMsg = 'Failed to create playing position';
      if (error.response?.status === 401) {
        errorMsg = 'Unauthorized. Please login again.';
      } else if (error.response?.status === 403) {
        errorMsg = 'Forbidden. Admin access required.';
      } else if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMsg = errors.join(', ');
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdatePosition = async () => {
    if (!editingPosition || !formData.name.trim() || !formData.abbreviation.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter position name and abbreviation",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const positionData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        abbreviation: formData.abbreviation.trim().toUpperCase(),
        is_active: formData.is_active,
        sort_order: formData.sort_order
      };
      
      await playingPositionsApi.update(editingPosition.id, positionData);
      
      toast({
        title: "Success",
        description: "Playing position updated successfully"
      });
      
      setIsEditDialogOpen(false);
      setEditingPosition(null);
      resetForm();
      fetchPlayingPositions();
    } catch (error: any) {
      console.error('Error updating playing position:', error);
      
      let errorMsg = 'Failed to update playing position';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      } else if (error.response?.data?.errors) {
        const errors = Object.values(error.response.data.errors).flat();
        errorMsg = errors.join(', ');
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeletePosition = async (id: number, name: string) => {
    try {
      await playingPositionsApi.delete(id);
      
      toast({
        title: "Success",
        description: `Playing position "${name}" deleted successfully`
      });
      
      fetchPlayingPositions();
    } catch (error: any) {
      console.error('Error deleting playing position:', error);
      
      let errorMsg = 'Failed to delete playing position';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: boolean) => {
    try {
      await playingPositionsApi.toggleStatus(id);
      
      toast({
        title: "Success",
        description: `Playing position ${currentStatus ? 'deactivated' : 'activated'} successfully`
      });
      
      fetchPlayingPositions();
    } catch (error: any) {
      console.error('Error toggling playing position status:', error);
      
      let errorMsg = 'Failed to toggle playing position status';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const handleReorder = async (positionId: number, direction: 'up' | 'down') => {
    const currentIndex = playingPositions.findIndex(p => p.id === positionId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= playingPositions.length) return;
    
    const reorderedPositions = [...playingPositions];
    const [movedPosition] = reorderedPositions.splice(currentIndex, 1);
    reorderedPositions.splice(newIndex, 0, movedPosition);
    
    // Update sort_order for all positions
    const positionsToUpdate = reorderedPositions.map((position, index) => ({
      id: position.id,
      sort_order: index + 1
    }));
    
    try {
      await playingPositionsApi.reorder(positionsToUpdate);
      
      toast({
        title: "Success",
        description: "Playing positions reordered successfully"
      });
      
      fetchPlayingPositions();
    } catch (error: any) {
      console.error('Error reordering playing positions:', error);
      
      let errorMsg = 'Failed to reorder playing positions';
      if (error.response?.data?.message) {
        errorMsg = error.response.data.message;
      }
      
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive"
      });
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setFormData(prev => ({ ...prev, sort_order: playingPositions.length + 1 }));
    setIsCreateDialogOpen(true);
  };

  const openEditDialog = (position: PlayingPosition) => {
    setEditingPosition(position);
    setFormData({
      name: position.name,
      description: position.description,
      abbreviation: position.abbreviation,
      is_active: position.is_active,
      sort_order: position.sort_order
    });
    setIsEditDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Playing Positions</h1>
            <p className="text-gray-600 mt-2">Manage basketball playing positions for member registration</p>
          </div>
          
          <Button onClick={openCreateDialog}>
            <Plus className="w-4 h-4 mr-2" />
            Add Playing Position
          </Button>
        </div>

        {playingPositions.length === 0 ? (
          <Card>
            <CardContent className="text-center py-8">
              <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Playing Positions</h3>
              <p className="text-gray-600 mb-4">Get started by creating your first playing position.</p>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Create Playing Position
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {playingPositions.map((position, index) => (
              <Card key={position.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg flex items-center">
                        <Trophy className="w-5 h-5 mr-2 text-blue-500" />
                        {position.name}
                        <Badge variant="outline" className="ml-2 text-xs">
                          {position.abbreviation}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="mt-1">
                        Order: {position.sort_order}
                      </CardDescription>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant={position.is_active ? "default" : "secondary"}>
                        {position.is_active ? "Active" : "Inactive"}
                      </Badge>
                      {position.members_count !== undefined && (
                        <Badge variant="outline">
                          {position.members_count} members
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4">
                    {position.description || 'No description'}
                  </p>
                  
                  <div className="flex justify-between items-center">
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReorder(position.id, 'up')}
                        disabled={index === 0}
                      >
                        <ArrowUp className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleReorder(position.id, 'down')}
                        disabled={index === playingPositions.length - 1}
                      >
                        <ArrowDown className="w-4 h-4" />
                      </Button>
                    </div>
                    
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleStatus(position.id, position.is_active)}
                      >
                        {position.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(position)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="outline">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Playing Position</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{position.name} ({position.abbreviation})"? This action cannot be undone.
                              {position.members_count && position.members_count > 0 && (
                                <span className="text-red-600 font-medium">
                                  <br />Warning: This position is currently used by {position.members_count} members.
                                </span>
                              )}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeletePosition(position.id, position.name)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Create Playing Position</DialogTitle>
              <DialogDescription>
                Add a new playing position for member classification
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Position Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Point Guard"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="abbreviation">Abbreviation *</Label>
                <Input
                  id="abbreviation"
                  value={formData.abbreviation}
                  onChange={(e) => setFormData(prev => ({ ...prev, abbreviation: e.target.value.toUpperCase() }))}
                  placeholder="e.g., PG"
                  maxLength={3}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this playing position..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="sort_order">Sort Order</Label>
                <Input
                  id="sort_order"
                  type="number"
                  min="1"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="is_active">Active</Label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsCreateDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleCreatePosition}
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Create Position'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Playing Position</DialogTitle>
              <DialogDescription>
                Update the playing position information
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Position Name *</Label>
                <Input
                  id="edit-name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Point Guard"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-abbreviation">Abbreviation *</Label>
                <Input
                  id="edit-abbreviation"
                  value={formData.abbreviation}
                  onChange={(e) => setFormData(prev => ({ ...prev, abbreviation: e.target.value.toUpperCase() }))}
                  placeholder="e.g., PG"
                  maxLength={3}
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="edit-description">Description</Label>
                <Textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe this playing position..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label htmlFor="edit-sort_order">Sort Order</Label>
                <Input
                  id="edit-sort_order"
                  type="number"
                  min="1"
                  value={formData.sort_order}
                  onChange={(e) => setFormData(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 1 }))}
                />
              </div>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="edit-is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                />
                <Label htmlFor="edit-is_active">Active</Label>
              </div>
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsEditDialogOpen(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="button" 
                onClick={handleUpdatePosition}
                disabled={isSubmitting}
              >
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Update Position'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPlayingPositions; 